import {Component, DestroyRef, Input, Output, EventEmitter, ChangeDetectorRef, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ConfigStub} from "../../_typing/config-stub";
import {AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors} from '@angular/forms';
import {MatError, MatSuffix} from "@angular/material/form-field";
import {ConstantProperty} from '../../_models/constant-property';
import {CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';
import {IDMap, KeyMap} from '../../_typing/generic-types';
import {getManyRelationWithIdFilter, getBaseFilter} from "../../services/api/filter_utils";
import {ComponentTypeJsonConfig} from "../../_typing/config/component-form-config";
import {ComponentNameFormulaBuilderService} from "../../services/component-name-formula-builder.service";
import {moment} from "../../services/timezone-selector.service";
import {ComponentType} from '../../_models/component-type';
import {ConstantPropertyDataService} from "../../data/constant-property-data.service";
import {deepCopy} from '../../lib/utils';
import {FormulaPart, FormulaPartType, NameFormula} from "../../_typing/config/component-name-formula-config";
import {DATE_FORMATS, DateFormat} from "../../_typing/utils/date-format";
import {COMMON_FORM_IMPORTS} from "../../shared/import-groups/Common";
import {
    SelectSearchComponentComponent
} from "../../components/select-search-api/single/select-search-component.component";
import {SharedPipesModule} from "../../shared/modules/shared-pipes.module";
import {
    ComponentNameFormulaTableComponent
} from "./component-name-formula-table/component-name-formula-table.component";
import {CommonModule} from "@angular/common";
import {MatMenu, MatMenuTrigger} from "@angular/material/menu";


@Component({
    selector: 'component-name-formula-builder',
    templateUrl: './component-name-formula-builder.component.html',
    styleUrls: ['./component-name-formula-builder.component.scss'],
    imports: [
        COMMON_FORM_IMPORTS,
        CommonModule,
        SelectSearchComponentComponent,
        CdkDropList,
        SharedPipesModule,
        ComponentNameFormulaTableComponent,
        CdkDrag,
        ReactiveFormsModule,
        MatMenuTrigger,
        MatMenu,
        MatError,
        MatSuffix
    ],
})
export class ComponentNameFormulaBuilderComponent {
    private readonly destroyRef = inject(DestroyRef);
    private _config: ComponentTypeJsonConfig;
    @Input() componentType: ConfigStub<ComponentType>;

    @Input() set config(config: ComponentTypeJsonConfig) {
        if (!config) config = {};
        this._config = config;
        this.loadForm();
    };

    get config(): ComponentTypeJsonConfig {
        return this._config;
    }

    selectedFormulaParts: FormulaPart[] = [];
    dateFormat: string = 'DDMMYYYY';
    selectedConstantProperty: ConstantProperty;
    cpDict: IDMap<ConstantProperty> = {};
    selectedSeparator: string = "_"
    availableSeparators: { symbol: string; title: string }[] = [
        {symbol: ' ', title: 'space'},
        {symbol: '_', title: 'underscore'},
        {symbol: '-', title: 'dash'},
        {symbol: '.', title: 'point'},
        {symbol: '/', title: 'slash'},
        {symbol: ':', title: 'colon'}
    ];
    // 'none' marks "no predefined format" — selected automatically once the user types a custom format.
    predefinedDateFormats: readonly ('none' | DateFormat)[] = ['none', ...DATE_FORMATS];
    private static readonly DATE_FORMAT_ALLOWED = /^[YMDdoHhmsSAaQEWwkZzXxT_/.:\- ]+$/;
    customDateFormat = new FormControl('', [ComponentNameFormulaBuilderComponent.dateFormatValidator]);
    listOrientation = 'horizontal';

    constantPropertyFilter: any[];
    initFormulaDict: KeyMap<NameFormula>;
    selectedKey: number = 0;

    @Output() configChanged: EventEmitter<ComponentTypeJsonConfig> = new EventEmitter();

    constructor(private propertyData: ConstantPropertyDataService,
                private formulaBuilderService: ComponentNameFormulaBuilderService,
                private cdr: ChangeDetectorRef) {
    }

    loadForm(): void {
        this.constantPropertyFilter = [getManyRelationWithIdFilter('component_types', this.componentType.id),
            {
                or: [getBaseFilter(false, 'is_calculation', 'eq'),
                    getBaseFilter(null, 'is_calculation', 'eq')]
            },
            getBaseFilter('file', 'data_type', 'ne')
        ];
        this.config.name_formulas = this.config.name_formulas || {};
        this.initFormulaDict = deepCopy(this.config.name_formulas);
        this.getProperties();
    }

    formulaSelected(i: string | number) {
        this.selectedKey = Number(i);
        this.selectedFormulaParts = this.config.name_formulas[this.selectedKey] || [];
        this.cdr.markForCheck();
    }

    formulaDeleted(i: string | number) {
        if (Number(i) === this.selectedKey) {
            this.selectedFormulaParts = [];
        }
    }

    addFormula() {
        const key = this.getNextKey();
        this.config.name_formulas[key] = [];
        this.formulaSelected(key);
        this.cdr.markForCheck();
    }

    reset(): void {
        this.selectedFormulaParts = this.initFormulaDict[this.selectedKey] || [];
        this.emitChange();
    }

    private getProperties(): void {
        this.formulaBuilderService.getProperties(this.config.name_formulas)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(result => {
                result?.data.forEach(cp => this.cpDict[cp.id] = cp);
                this.cpDict = Object.assign({}, this.cpDict);
            })
    }

    private addToFormula(type: FormulaPartType, content: string): void {
        this.selectedFormulaParts.push({type: type, content: content});
        this.updateConfig();
    }

    addDateToFormula(format: string): void {
        if (!format) return;
        // Re-check a custom (non-predefined) format through moment before committing it —
        // we never add an unparseable date format to the formula.
        const isPredefined = (DATE_FORMATS as readonly string[]).includes(format);
        if (!isPredefined && !ComponentNameFormulaBuilderComponent.isValidCustomFormat(format)) {
            this.customDateFormat.setErrors({invalidDateFormat: true});
            return;
        }
        this.addToFormula('date', format);
    }

    /**
     * A custom date format is valid when it uses only allowed tokens/separators AND moment
     * actually interprets it as a date — i.e. formatting a date yields output that differs
     * from the format string (a token-less string like '----' formats back to itself).
     */
    static isValidCustomFormat(format: string): boolean {
        if (!format) return false;
        if (!ComponentNameFormulaBuilderComponent.DATE_FORMAT_ALLOWED.test(format)) return false;
        try {
            const sample = moment().format(format);
            return !!sample && sample !== format;
        } catch {
            return false;
        }
    }

    static dateFormatValidator(control: AbstractControl): ValidationErrors | null {
        const value: string = control.value;
        if (!value) return null;  // empty handled by the button/predefined selection
        return ComponentNameFormulaBuilderComponent.isValidCustomFormat(value) ? null : {invalidDateFormat: true};
    }


    /** The format the 'Add to formula' button will use: the custom one if typed, else the predefined selection. */
    get selectedDateFormatToAdd(): string {
        const custom = (this.customDateFormat.value || '').trim();
        if (custom) return custom;
        return this.dateFormat && this.dateFormat !== 'none' ? this.dateFormat : '';
    }

    /** Typing a custom format clears the predefined selection (shows 'none'). */
    onCustomFormatInput(event: Event): void {
        if ((event.target as HTMLInputElement).value) {
            this.dateFormat = 'none';
        }
    }

    /** Choosing a real predefined format clears any half-typed custom format. */
    onPredefinedSelected(value: string): void {
        if (value !== 'none') {
            this.customDateFormat.setValue('');
        }
    }

    addSeparatorToFormula(separator: string): void {
        this.addToFormula('separator', separator);
    }

    addCountToFormula(): void {
        this.addToFormula('count', '[Count]');
    }

    addPropertyToFormula(value: ConstantProperty): void {
        this.addToFormula('constant_property', value.id);
        this.cpDict[value.id] = value;
        this.cdr.markForCheck();
    }

    addTextToFormula(text: string): void {
        this.addToFormula('text', text);
    }

    hasCount(): boolean {
        return this.selectedFormulaParts?.some(fp => fp.type === 'count');
    }

    hasDate(): boolean {
        return this.selectedFormulaParts?.some(fp => fp.type === 'date');
    }

    drop(event: CdkDragDrop<string[]>): void {
        moveItemInArray(this.selectedFormulaParts, event.previousIndex, event.currentIndex);
        this.updateConfig();
    }

    removeItem(index: number): void {
        this.selectedFormulaParts.splice(index, 1);
        this.updateConfig();
        this.selectedKey = this.selectDefault();
    }

    private emitChange(): void {
        this.configChanged.emit(this.config);
    }

    private updateConfig(): void {
        this.config.name_formulas[this.selectedKey] = this.selectedFormulaParts;
        this.emitChange();
    }

    private selectDefault(): number {
        const keys: number[] = Object.keys(this.config.name_formulas)?.map(k => Number(k));
        return keys?.length ? Math.min(...keys) : 0;
    }

    getNextKey(): number {
        const keys: number[] = Object.keys(this.config.name_formulas)?.map(k => Number(k));
        return keys?.length ? Math.max(...keys) + 1 : 0;
    }
}
